import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import { useTransmitters, useAllAccounts, transmittersKeys } from '../hooks/useTransmitters';
import {
  TransmittersTable,
  COLUMNS,
  loadColumnVisibility,
  saveColumnVisibility,
} from '../components/TransmittersTable';
import type { ColumnDef } from '../components/TransmittersTable';
import { TransmitterEditModal } from '../components/TransmitterEditModal';
import ReassignDialog from '@/features/accounts/components/ReassignDialog';
import { exportToCSV, copyToClipboard } from '../utils/csvExport';
import type { Transmitter, TransmittersFilters } from '../types';
import { TRANSMITTER_PERMISSIONS } from '../permissions';
import { useHasTransmitterPermission } from '../hooks/useTransmitterPermission';

// =============================================================================
// Props
// =============================================================================

export interface TransmittersPageProps {
  title?: string;
  subtitle?: string;
  columns?: ColumnDef<Transmitter>[];
  storageKeyPrefix?: string;
  apiParams?: Partial<TransmittersFilters>;
  exportFilename?: string;
  emptyMessage?: string;
  deviceType?: 'transmitters' | 'gateways';
  onTransmittersChange?: (transmitters: Transmitter[]) => void;
}

// =============================================================================
// Page Component
// =============================================================================

export function TransmittersPage({
  title = 'Transmitters',
  subtitle = 'IoT device management',
  columns = COLUMNS,
  storageKeyPrefix = 'atek-transmitters',
  apiParams = {},
  exportFilename = 'transmitters',
  emptyMessage = 'No transmitters found',
  deviceType = 'transmitters',
  onTransmittersChange,
}: TransmittersPageProps = {}) {
  const queryClient = useQueryClient();
  const canEditTransmitters = useHasTransmitterPermission(TRANSMITTER_PERMISSIONS.edit);
  const canReassignTransmitters = useHasTransmitterPermission(TRANSMITTER_PERMISSIONS.reassign);
  const filterStorageKey = `${storageKeyPrefix}-column-filters`;
  const visibilityStorageKey = `${storageKeyPrefix}-column-visibility`;

  // ---------------------------------------------------------------------------
  // Column filter state (persisted to localStorage)
  // ---------------------------------------------------------------------------
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    () => {
      try {
        return JSON.parse(
          localStorage.getItem(filterStorageKey) || '{}',
        );
      } catch {
        return {};
      }
    },
  );

  useEffect(() => {
    localStorage.setItem(filterStorageKey, JSON.stringify(columnFilters));
  }, [columnFilters, filterStorageKey]);

  // Server-promoted filter keys: page must reset when these change
  const SERVER_FILTER_KEYS = ['image', 'account'];

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    // Batch page reset with filter change to avoid async useEffect → setState
    // re-render cascade that triggers Radix UI ref infinite loop
    if (SERVER_FILTER_KEYS.includes(key)) setPage(0);
  };

  // ---------------------------------------------------------------------------
  // Column visibility state (persisted to localStorage)
  // ---------------------------------------------------------------------------
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => loadColumnVisibility(visibilityStorageKey, columns));

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated, visibilityStorageKey);
  };

  // ---------------------------------------------------------------------------
  // Sort state
  // ---------------------------------------------------------------------------
  const [sortKey, setSortKey] = useState<string>('dev_id');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(key === 'lastpacket_at' || key === 'created_at' ? -1 : 1);
    }
  };

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------
  const [page, setPage] = useState(0);
  const pageLength = 50;

  // ---------------------------------------------------------------------------
  // Extract server-side search from column filters (generalSearch)
  // The BE search param matches against devId, name, and account name via ILIKE
  // ---------------------------------------------------------------------------
  const devIdFilter = columnFilters['dev_id'] || '';
  const nameFilter = columnFilters['name'] || '';
  const accountFilter = columnFilters['account'] || '';
  // If account filter is a JSON array (from dropdown), don't include in text search
  const isAccountDropdown = accountFilter.startsWith('[');
  const searchTerm = [devIdFilter, nameFilter, isAccountDropdown ? '' : accountFilter].filter(Boolean).join(' ');
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Parse account IDs from dropdown selection
  const accountIdIn = useMemo<string[] | undefined>(() => {
    if (!isAccountDropdown) return undefined;
    try {
      const ids = JSON.parse(accountFilter) as string[];
      return ids.length > 0 ? ids : undefined;
    } catch { return undefined; }
  }, [accountFilter, isAccountDropdown]);

  // Extract server-side image filter from column filters
  const imageFilterRaw = columnFilters['image'] || '';
  const hasImageFilter = useMemo<'yes' | 'no' | undefined>(() => {
    if (!imageFilterRaw) return undefined;
    try {
      const selected = JSON.parse(imageFilterRaw) as string[];
      if (selected.length === 1) return selected[0] as 'yes' | 'no';
    } catch { /* not valid JSON, ignore */ }
    return undefined;
  }, [imageFilterRaw]);

  // Reset page when debounced search or sort changes
  // (select filter page resets are batched inline in setColumnFilter)
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, sortKey, sortDirection]);

  // ---------------------------------------------------------------------------
  // Transmitter detail sheet
  // ---------------------------------------------------------------------------
  const [selectedTransmitterId, setSelectedTransmitterId] = useState<
    string | null
  >(null);

  // ---------------------------------------------------------------------------
  // Reassign state
  // ---------------------------------------------------------------------------
  const [reassignTransmitter, setReassignTransmitter] = useState<Transmitter | null>(null);

  // ---------------------------------------------------------------------------
  // API query
  // ---------------------------------------------------------------------------
  const filters: TransmittersFilters = {
    generalSearch: debouncedSearch || undefined,
    accountIdIn,
    pageNumber: page,
    pageLength: pageLength,
    sortKey: sortKey,
    sortDirection: sortDirection,
    hasImage: hasImageFilter,
    ...apiParams,
  };

  const transmittersQuery = useTransmitters(filters);
  const accountsQuery = useAllAccounts();

  const transmitters = transmittersQuery.data?.transmitters || [];
  const total = transmittersQuery.data?.total || 0;
  const totalPages = Math.ceil(total / pageLength);

  // Build searchable-select options from accounts data
  const searchableSelectOptions = useMemo(() => ({
    account: (accountsQuery.data || [])
      .map((a) => ({ label: a.name, value: a.id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  }), [accountsQuery.data]);

  // ---------------------------------------------------------------------------
  // Client-side column filtering (skip server-side columns: dev_id, name)
  // ---------------------------------------------------------------------------
  const filteredTransmitters = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(
      ([key, v]) => v && key !== 'dev_id' && key !== 'name' && key !== 'account' && key !== 'image',
    );
    if (activeFilters.length === 0) return transmitters;

    return transmitters.filter((transmitter) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = columns.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(transmitter);

        // Date range: "from>>to"
        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        // Multi-select: JSON array
        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return (
              selected.length === 0 ||
              selected.includes(String(rawValue || ''))
            );
          } catch {
            return true;
          }
        }

        // Number range: "min>>max"
        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        // Text: case-insensitive includes
        return String(rawValue ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      }),
    );
  }, [transmitters, columnFilters, columns]);

  // Notify parent when visible transmitters change
  useEffect(() => {
    onTransmittersChange?.(filteredTransmitters);
  }, [filteredTransmitters, onTransmittersChange]);

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------
  const handleExportCSV = () => {
    exportToCSV(filteredTransmitters, columns, exportFilename);
    toast.success(`Exported ${filteredTransmitters.length} rows`);
  };

  const handleCopyClipboard = () => {
    copyToClipboard(filteredTransmitters, columns);
    toast.success('Copied to clipboard');
  };

  // ---------------------------------------------------------------------------
  // Refresh
  // ---------------------------------------------------------------------------
  const handleRefresh = () => {
    transmittersQuery.refetch();
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            {title}{' '}
            <small>{subtitle}</small>
          </h1>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={transmittersQuery.isFetching}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${transmittersQuery.isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      {/* Table */}
      {transmittersQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">
              Error loading transmitters. Please try again.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={handleRefresh}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TransmittersTable
          transmitters={filteredTransmitters}
          isLoading={transmittersQuery.isLoading}
          total={total}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          columnFilters={columnFilters}
          setColumnFilter={setColumnFilter}
          columnVisibility={columnVisibility}
          toggleColumn={toggleColumn}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          onExportCSV={handleExportCSV}
          onCopyClipboard={handleCopyClipboard}
          columns={columns}
          emptyMessage={emptyMessage}
          onEdit={canEditTransmitters ? (t) => setSelectedTransmitterId(t._id) : undefined}
          onReassign={canReassignTransmitters ? setReassignTransmitter : undefined}
          searchableSelectOptions={searchableSelectOptions}
          onClearColumnFilters={() => setColumnFilters({})}
        />
      )}

      {/* Transmitter Edit Modal */}
      <TransmitterEditModal
        transmitterId={selectedTransmitterId}
        open={!!selectedTransmitterId}
        onOpenChange={(open) => {
          if (!open) setSelectedTransmitterId(null);
        }}
        deviceType={deviceType}
      />

      {/* Reassign Dialog */}
      {canReassignTransmitters && (
        <ReassignDialog
          entityType="transmitter"
          entityId={reassignTransmitter?._id || ''}
          entityLabel={reassignTransmitter?.name || reassignTransmitter?.dev_id || ''}
          currentAccountId={
            reassignTransmitter?.organisation && typeof reassignTransmitter.organisation === 'object'
              ? reassignTransmitter.organisation._id
              : (typeof reassignTransmitter?.organisation === 'string' ? reassignTransmitter.organisation : '')
          }
          currentAccountName={
            reassignTransmitter?.organisation && typeof reassignTransmitter.organisation === 'object'
              ? reassignTransmitter.organisation.name
              : ''
          }
          open={!!reassignTransmitter}
          onOpenChange={(open) => {
            if (!open) {
              setReassignTransmitter(null);
              queryClient.invalidateQueries({ queryKey: transmittersKeys.all });
            }
          }}
        />
      )}
    </div>
  );
}

export default TransmittersPage;
